import { PrayerTimes } from "../types/PrayerTypes";

// The Masjid Clock's simple view, step for step (Masjid247Clock main.cpp: buildSimpleViewBlock,
// chooseFridaySlotActiveOrNext, isNearJamatWindow, makePrayerBlock). One prayer at a time, kept
// until 5 minutes after its jama'ah; Sunrise and each Jumu'ah are steps of their own.
const GRACE_MIN = 5;
const NEAR_JAMAAH_MIN = 15;

export type NextPrayerKey = "fajr" | "sunrise" | "jummah" | "dhuhr" | "asar" | "maghrib" | "isha";

export interface NextPrayerStep {
  key: NextPrayerKey;
  tomorrow: boolean;
  begins?: string;
  jamaah?: string;
  // Fridays: which Jumu'ah this is, and how many the masjid holds.
  slot?: number;
  slots?: number;
  // Only one card is lit at a time: Begins from its time, then Jama'ah from 15 minutes before.
  beginsLit: boolean;
  jamaahLit: boolean;
}

export const toMinutes = (time?: string): number | null => {
  const m = /^(\d{1,2}):(\d{2})/.exec((time || "").trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

const has = (time?: string) => toMinutes(time) !== null;

// A begins time equal to the jama'ah (Maghrib, Isha in summer) doesn't count as its own moment.
const effectiveBegins = (begins?: string, jamaah?: string): number | null => {
  const b = toMinutes(begins);
  const j = toMinutes(jamaah);
  return j === null || b === null || b === j ? null : b;
};

const nearJamaah = (begins: string | undefined, jamaah: string | undefined, now: number): boolean => {
  const j = toMinutes(jamaah);
  if (j === null) return false;
  const diff = j - now;
  const eff = effectiveBegins(begins, jamaah);
  // With no separate begins, or less than 15 minutes between them, only from the jama'ah itself.
  if (eff === null || j - NEAR_JAMAAH_MIN < eff) return diff >= -GRACE_MIN && diff <= 0;
  if (now < eff) return false;
  return diff <= NEAR_JAMAAH_MIN && diff >= -GRACE_MIN;
};

const step = (
  key: NextPrayerKey,
  begins: string | undefined,
  jamaah: string | undefined,
  now: number,
  extra: Partial<NextPrayerStep> = {},
): NextPrayerStep => {
  const b = toMinutes(begins);
  const jamaahLit = nearJamaah(begins, jamaah, now);
  return {
    key,
    tomorrow: false,
    begins: has(begins) ? begins : undefined,
    jamaah: has(jamaah) ? jamaah : undefined,
    beginsLit: b !== null && now >= b && !jamaahLit,
    jamaahLit,
    ...extra,
  };
};

const fridaySlot = (today: PrayerTimes, now: number): Partial<NextPrayerStep> | null => {
  const slots = [today.Jummah1, today.Jummah2, today.Jummah3, today.Jummah4].filter(has);
  for (let i = 0; i < slots.length; i++) {
    if (now <= toMinutes(slots[i])! + GRACE_MIN) return { jamaah: slots[i], slot: i + 1, slots: slots.length };
  }
  const dhuhr = toMinutes(today.JamDhuhr);
  if (slots.length === 0 && dhuhr !== null && now <= dhuhr + GRACE_MIN) {
    return { jamaah: today.JamDhuhr, slot: 1, slots: 1 };
  }
  return null;
};

// `now` is minutes since midnight in the masjid's time zone.
export function nextPrayerStep(
  today: PrayerTimes,
  tomorrow: PrayerTimes | null | undefined,
  now: number,
  isFriday: boolean,
): NextPrayerStep | null {
  const fajrBegins = toMinutes(today.BegFajr);
  const fajrJamaah = toMinutes(today.JamFajr);
  const sunrise = toMinutes(today.Sunrise);
  const pending = (begins?: string, jamaah?: string) =>
    (has(begins) || has(jamaah)) && (!has(jamaah) || now < toMinutes(jamaah)! + GRACE_MIN);

  if (
    fajrBegins !== null &&
    (fajrJamaah === null ? sunrise === null || now < sunrise : now < fajrJamaah + GRACE_MIN)
  ) {
    return step("fajr", today.BegFajr, today.JamFajr, now);
  }

  if (sunrise !== null && now < sunrise) {
    return { key: "sunrise", tomorrow: false, begins: today.Sunrise, beginsLit: false, jamaahLit: false };
  }

  if (isFriday) {
    const slot = fridaySlot(today, now);
    if (slot) return step("jummah", today.BegDhuhr, slot.jamaah, now, slot);
  }

  if (!isFriday && pending(today.BegDhuhr, today.JamDhuhr)) return step("dhuhr", today.BegDhuhr, today.JamDhuhr, now);
  if (pending(today.BegAsar, today.JamAsar)) return step("asar", today.BegAsar, today.JamAsar, now);
  if (pending(today.BegMaghrib, today.JamMaghrib)) return step("maghrib", today.BegMaghrib, today.JamMaghrib, now);
  if (pending(today.BegEsha, today.JamEsha)) return step("isha", today.BegEsha, today.JamEsha, now);

  if (tomorrow && has(tomorrow.BegFajr)) {
    return {
      key: "fajr",
      tomorrow: true,
      begins: tomorrow.BegFajr,
      jamaah: has(tomorrow.JamFajr) ? tomorrow.JamFajr : undefined,
      beginsLit: false,
      jamaahLit: false,
    };
  }
  return null;
}
